import type { WorkspaceDesk } from '@/config/workspace-desk'
import { isNonProductionWorkspace } from '@/config/public-env'
import { PayrollPermissions } from '@/features/payroll/permissions'
import { SalesPermissions, isSalesFeatureEnabled } from '@/features/sales/permissions'
import { StockPermissions } from '@/features/stocks/permissions'

/** Platform staffing role for the HR desk (`Roles.Hr` on the API). */
export const HR_PLATFORM_ROLE = 'Hr'

/** Platform staffing role for the PRO desk (`Roles.Pro` on the API). */
export const PRO_PLATFORM_ROLE = 'Pro'

/** PRO assistant. The PRO account chooses which desk areas this login can open. */
export const PRO_ASSISTANT_PLATFORM_ROLE = 'ProAssistant'

/** Platform staffing role for the Accounts desk (`Roles.Accounts` on the API). */
export const ACCOUNTS_PLATFORM_ROLE = 'Accounts'

/** Desk roles that can be combined; 2+ → `app.amglobal.co` home. */
const COMBINABLE_DESK_ROLES = new Set([
  'Hr',
  'Pro',
  'ProAssistant',
  'Recruiter',
  'Reception',
  'Onboarding',
  'Accounts',
  'Stocks',
])

export type StaffCapabilitySet = ReadonlySet<string>

export function staffCapabilitySet(capabilities: readonly string[]): StaffCapabilitySet {
  return new Set(capabilities)
}

function hasCap(caps: StaffCapabilitySet, key: string): boolean {
  return caps.has(key)
}

export function isDocumentFilingOnly(
  capabilities: readonly string[] | StaffCapabilitySet,
): boolean {
  const caps = normalizeCapabilitySet(capabilities)
  if (!hasCap(caps, 'document_filing_submit')) return false
  for (const capability of caps) {
    if (capability !== 'document_filing_submit') return false
  }
  return true
}

function normalizeCapabilitySet(
  capabilities: readonly string[] | StaffCapabilitySet,
): StaffCapabilitySet {
  if (Array.isArray(capabilities)) {
    return staffCapabilitySet(capabilities)
  }
  return capabilities as StaffCapabilitySet
}

export function canAdminQueryRecruitment(caps: StaffCapabilitySet): boolean {
  return hasCap(caps, 'admin_query_recruitment')
}

export function canManageOnboarding(caps: StaffCapabilitySet): boolean {
  return hasCap(caps, 'manage_onboarding')
}

export function canSendOnboardingInvite(caps: StaffCapabilitySet): boolean {
  return hasCap(caps, 'manage_onboarding') && hasCap(caps, 'onboarding_invite_send')
}

export function canManagePayroll(caps: StaffCapabilitySet): boolean {
  return hasCap(caps, PayrollPermissions.manage)
}

export function canAccessRecruitmentJobBoard(caps: StaffCapabilitySet): boolean {
  return hasCap(caps, 'manage_job_posts') || canAdminQueryRecruitment(caps)
}

export function canAccessApplicantsPipeline(caps: StaffCapabilitySet): boolean {
  return (
    hasCap(caps, 'manage_candidates') ||
    hasCap(caps, 'manage_interviews') ||
    canAdminQueryRecruitment(caps)
  )
}

export function canAccessHrOnboardingWorkspace(caps: StaffCapabilitySet): boolean {
  return (
    canManageOnboarding(caps) ||
    hasCap(caps, 'onboarding_walk_in_create') ||
    hasCap(caps, 'onboarding_employee_access_manage') ||
    canAdminQueryRecruitment(caps) ||
    hasCap(caps, 'manage_users')
  )
}

export function shouldUseAdminPortal(caps: StaffCapabilitySet): boolean {
  return canAdminQueryRecruitment(caps) || hasCap(caps, 'manage_users')
}

export function isClientOnly(caps: StaffCapabilitySet): boolean {
  return (
    hasCap(caps, 'view_client_applicants') &&
    !canAccessApplicantsPipeline(caps) &&
    !canAccessRecruitmentJobBoard(caps) &&
    !canAdminQueryRecruitment(caps)
  )
}

export function isReceptionOnly(caps: StaffCapabilitySet): boolean {
  return (
    hasCap(caps, 'onboarding_walk_in_create') &&
    !canManageOnboarding(caps) &&
    !hasCap(caps, 'onboarding_employee_access_manage') &&
    !canAccessRecruitmentJobBoard(caps) &&
    !canAccessApplicantsPipeline(caps) &&
    !canAdminQueryRecruitment(caps) &&
    !hasCap(caps, 'manage_users') &&
    !hasCap(caps, 'view_client_applicants') &&
    !canManagePayroll(caps)
  )
}

export function isProPortalOnly(caps: StaffCapabilitySet): boolean {
  return (
    !shouldUseAdminPortal(caps) &&
    !isReceptionOnly(caps) &&
    hasCap(caps, 'pro_case_manage') &&
    !canManageOnboarding(caps) &&
    !canManagePayroll(caps) &&
    !canAccessRecruitmentJobBoard(caps)
  )
}

export function isRecruiterPortalOnly(caps: StaffCapabilitySet): boolean {
  return (
    !shouldUseAdminPortal(caps) &&
    !isReceptionOnly(caps) &&
    canAccessRecruitmentJobBoard(caps) &&
    !canApproveAttendance(caps) &&
    !canManageOnboarding(caps) &&
    !canManagePayroll(caps)
  )
}

export function isHrPortalOnly(caps: StaffCapabilitySet): boolean {
  return (
    !shouldUseAdminPortal(caps) &&
    !isReceptionOnly(caps) &&
    !isProPortalOnly(caps) &&
    !canAccessRecruitmentJobBoard(caps) &&
    canManagePayroll(caps) &&
    canManageOnboarding(caps) &&
    !hasCap(caps, PayrollPermissions.accountsWorkspace)
  )
}

export function userHasAccountsPlatformRole(roles: readonly string[]): boolean {
  return roles.some((role) => role === ACCOUNTS_PLATFORM_ROLE)
}

/** Accounts desk — Accounts role with the accounts workspace capability. */
export function isAccountsPortalOnly(
  roles: readonly string[],
  capabilities: readonly string[] | StaffCapabilitySet,
): boolean {
  const caps = normalizeCapabilitySet(capabilities)
  return (
    userHasAccountsPlatformRole(roles) &&
    hasCap(caps, PayrollPermissions.accountsWorkspace) &&
    !shouldUseAdminPortal(caps) &&
    !canManageOnboarding(caps) &&
    // Accounts may operate hold/adjustments/second runs (pay period + supplemental);
    // stay off identity lock and attendance mark correction (HR).
    !hasCap(caps, PayrollPermissions.identityManage) &&
    !hasCap(caps, PayrollPermissions.attendanceCorrect)
  )
}

/** Field supervisor desk — attendance capture without back-office payroll admin. */
export function canApproveAttendance(caps: StaffCapabilitySet): boolean {
  return hasCap(caps, PayrollPermissions.approveAttendance)
}

function hasAnyCap(caps: StaffCapabilitySet, keys: readonly string[]): boolean {
  return keys.some((k) => caps.has(k))
}

/** Ops stocks workspace (not supervisor material-request alone). */
export function canAccessStocksWorkspace(caps: StaffCapabilitySet): boolean {
  return hasAnyCap(caps, StockPermissions.workspaceAccess)
}

export function isSupervisorPortalOnly(caps: StaffCapabilitySet): boolean {
  return (
    canApproveAttendance(caps) &&
    !shouldUseAdminPortal(caps) &&
    !isReceptionOnly(caps) &&
    !isProPortalOnly(caps) &&
    !canManageOnboarding(caps) &&
    !canManagePayroll(caps) &&
    !hasCap(caps, 'view_client_applicants')
  )
}

export function isOnboardingPortalOnly(caps: StaffCapabilitySet): boolean {
  return (
    !shouldUseAdminPortal(caps) &&
    !isReceptionOnly(caps) &&
    !isProPortalOnly(caps) &&
    !hasCap(caps, 'pro_case_manage') &&
    !hasCap(caps, 'employee_exit_manage') &&
    !canAccessRecruitmentJobBoard(caps) &&
    canAccessHrOnboardingWorkspace(caps) &&
    !canManagePayroll(caps)
  )
}

export function userHasProPlatformRole(roles: readonly string[]): boolean {
  return roles.some((role) => role === PRO_PLATFORM_ROLE)
}

/** PRO owner or PRO assistant. Both land on the PRO desk. */
export function userHasProDeskRole(roles: readonly string[]): boolean {
  return roles.some(
    (role) => role === PRO_PLATFORM_ROLE || role === PRO_ASSISTANT_PLATFORM_ROLE,
  )
}

/** PRO account that owns the desk admin panel. Assistants do not. */
export function isProDeskSuperAdmin(roles: readonly string[]): boolean {
  return roles.some((role) => role === PRO_PLATFORM_ROLE)
}

/**
 * HR roster operations (import, employment status, deployability).
 * PRO may edit profile and documents without these.
 */
export function canRunHrRosterOperations(roles: readonly string[]): boolean {
  const hrRoster = roles.some(
    (role) =>
      role === 'Hr' ||
      role === 'Administrator' ||
      role === 'SystemAdministrator' ||
      role === 'Onboarding',
  )
  if (hrRoster) return true
  if (userHasProDeskRole(roles)) return false
  // Directory-list purge and import stay off a supervisor login.
  // Person edits on one employee use canEditEmployeeRoster.
  if (roles.some((role) => role === 'Supervisor')) return false
  return true
}

/**
 * Supervisor directory duty can open field staff only.
 * HR, onboarding, and administrators keep in-house records.
 */
export function hidesInHouseStaffRecords(
  roles: readonly string[],
  capabilities: readonly string[],
): boolean {
  if (!capabilities.includes('hr_employee_directory')) return false
  return !roles.some(
    (role) =>
      role === 'Hr' ||
      role === 'Onboarding' ||
      role === 'Administrator' ||
      role === 'SystemAdministrator',
  )
}

/** Status, deployability, and rehire on one employee page. */
export function canEditEmployeeRoster(
  roles: readonly string[],
  capabilities: readonly string[] = [],
): boolean {
  if (canRunHrRosterOperations(roles)) return true
  return capabilities.includes('hr_employee_directory')
}

/**
 * HR desk member: Hr is their only combinable office role.
 * Capability fingerprints must not send this person to the admin portal.
 */
export function isHrDeskRole(roles: readonly string[]): boolean {
  if (roles.some((role) => role === 'Administrator' || role === 'SystemAdministrator')) {
    return false
  }
  if (!roles.some((role) => role === HR_PLATFORM_ROLE)) return false
  let combinable = 0
  for (const role of roles) {
    if (COMBINABLE_DESK_ROLES.has(role)) combinable += 1
  }
  return combinable === 1
}

/** Two or more combinable desk roles → use app.amglobal.co (union dashboard). */
export function needsMultiRoleAppDesk(roles: readonly string[]): boolean {
  if (roles.some((r) => r === 'Administrator')) return false
  let count = 0
  for (const role of roles) {
    if (COMBINABLE_DESK_ROLES.has(role)) count += 1
  }
  return count >= 2
}

/**
 * Day-to-day owner Administrator (not break-glass SystemAdministrator).
 * UX shell only — API still authorizes via capabilities/policies.
 */
export function isThinAdmin(roles: readonly string[]): boolean {
  const set = new Set(roles)
  return set.has('Administrator') && !set.has('SystemAdministrator')
}

/** PRO desk session. Assistants stay on this desk even when only some areas are assigned. */
export function isProWorkspaceUser(
  roles: readonly string[],
  capabilities: readonly string[] | StaffCapabilitySet,
): boolean {
  const caps = normalizeCapabilitySet(capabilities)
  if (
    roles.some((role) => role === PRO_ASSISTANT_PLATFORM_ROLE) &&
    !userHasProPlatformRole(roles)
  ) {
    return true
  }
  return (
    userHasProPlatformRole(roles) &&
    (hasCap(caps, 'pro_case_manage') ||
      hasCap(caps, 'employee_exit_manage') ||
      hasCap(caps, 'pro_employee_directory'))
  )
}

/** Visa cases. Assistants need this area assigned by the PRO account. */
export function proOnboardingWorkspaceAllowed(
  capabilities: readonly string[] | StaffCapabilitySet,
  roles: readonly string[] = [],
): boolean {
  const caps = normalizeCapabilitySet(capabilities)
  if (userHasProDeskRole(roles) && hasCap(caps, 'pro_case_manage')) return true
  return isNonProductionWorkspace() && shouldUseAdminPortal(caps)
}

/** Employee directory on the PRO desk. Assistants need this area assigned. */
export function proEmployeeDirectoryNavAllowed(
  capabilities: readonly string[] | StaffCapabilitySet,
  roles: readonly string[] = [],
): boolean {
  const caps = normalizeCapabilitySet(capabilities)
  if (userHasProDeskRole(roles) && hasCap(caps, 'pro_employee_directory')) return true
  return isNonProductionWorkspace() && shouldUseAdminPortal(caps)
}

export function canManageEmployeeExit(
  capabilities: readonly string[] | StaffCapabilitySet,
  _roles: readonly string[] = [],
): boolean {
  const caps = normalizeCapabilitySet(capabilities)
  if (shouldUseAdminPortal(caps)) return true
  return hasCap(caps, 'employee_exit_manage')
}

/** Exit desk — PRO, admin, or HR (anyone with employee_exit_manage). */
export function proExitWorkspaceAllowed(
  capabilities: readonly string[] | StaffCapabilitySet,
  roles: readonly string[] = [],
): boolean {
  return canManageEmployeeExit(capabilities, roles)
}

/**
 * Default desk subdomain for this session (UX routing only — API enforces authorization).
 * Prefer [roles] when available so multi-role users land on `app`.
 */
export function resolveStaffHomeDesk(
  capabilities: readonly string[],
  roles: readonly string[] = [],
): WorkspaceDesk {
  if (needsMultiRoleAppDesk(roles)) return 'app'
  if (isHrDeskRole(roles)) return 'hr'

  const caps = staffCapabilitySet(capabilities)
  if (isClientOnly(caps)) return 'client'
  if (isReceptionOnly(caps)) return 'reception'
  if (shouldUseAdminPortal(caps)) return 'admin'
  if (isProWorkspaceUser(roles, caps) || isProPortalOnly(caps)) return 'pro'
  if (isRecruiterPortalOnly(caps)) return 'recruiter'
  if (isOnboardingPortalOnly(caps)) return 'onboarding'
  if (isAccountsPortalOnly(roles, caps)) return 'finance'
  if (isHrPortalOnly(caps)) return 'hr'
  if (isSupervisorPortalOnly(caps)) return 'supervisor'
  if (isDocumentFilingOnly(capabilities)) return 'app'

  // Capability mix without a unique persona shell → shared multi-role home.
  if (
    !shouldUseAdminPortal(caps) &&
    (hasCap(caps, 'pro_case_manage') ||
      canManageOnboarding(caps) ||
      canManagePayroll(caps) ||
      hasCap(caps, PayrollPermissions.accountsWorkspace) ||
      canAccessRecruitmentJobBoard(caps) ||
      hasCap(caps, 'onboarding_walk_in_create') ||
      canApproveAttendance(caps) ||
      canAccessStocksWorkspace(caps))
  ) {
    return 'app'
  }

  return 'admin'
}

/** Whether the signed-in session may use this desk hostname. */
export function isStaffDeskAllowed(
  desk: WorkspaceDesk,
  capabilities: readonly string[],
  roles: readonly string[] = [],
): boolean {
  const caps = staffCapabilitySet(capabilities)

  // Admin-only desks — evaluate before persona early-returns.
  if (desk === 'sales') {
    if (!isSalesFeatureEnabled()) return false
    return (
      shouldUseAdminPortal(caps) || caps.has(SalesPermissions.manage)
    )
  }
  if (desk === 'operations') {
    return shouldUseAdminPortal(caps)
  }

  // Multi-role shared home — capability-gated nav (union), not a frozen persona shell.
  if (desk === 'app') {
    if (shouldUseAdminPortal(caps)) return false
    if (needsMultiRoleAppDesk(roles)) return true
    if (isDocumentFilingOnly(capabilities)) return true
    return resolveStaffHomeDesk(capabilities, roles) === 'app'
  }

  if (isClientOnly(caps)) return desk === 'client'

  // Multi-role users stay on app.*; persona hosts freeze to single-role shells.
  if (needsMultiRoleAppDesk(roles)) {
    return false
  }

  if (isReceptionOnly(caps)) return desk === 'reception'
  if (isHrDeskRole(roles)) return desk === 'hr'
  if (shouldUseAdminPortal(caps)) {
    if (desk === 'admin' || desk === 'hr') return true
    // Local/non-prod: admin may preview persona desks (Accounts, PRO, Supervisor).
    if (
      isNonProductionWorkspace() &&
      (desk === 'pro' || desk === 'supervisor' || desk === 'finance')
    ) {
      return true
    }
    return false
  }

  if (desk === 'pro') return isProPortalOnly(caps) || isProWorkspaceUser(roles, caps)
  if (isProPortalOnly(caps)) return false

  if (isRecruiterPortalOnly(caps)) return desk === 'recruiter'
  if (desk === 'onboarding') return isOnboardingPortalOnly(caps)
  if (desk === 'finance') return isAccountsPortalOnly(roles, caps)
  if (desk === 'hr') return isHrPortalOnly(caps)
  if (desk === 'supervisor') {
    return isSupervisorPortalOnly(caps)
  }
  return desk === resolveStaffHomeDesk(capabilities, roles)
}

export function workspaceDeskDisplayName(desk: WorkspaceDesk): string {
  switch (desk) {
    case 'admin':
      return 'Admin'
    case 'app':
      return 'Workspace'
    case 'hr':
      return 'HR'
    case 'pro':
      return 'PRO'
    case 'recruiter':
      return 'Recruiter'
    case 'reception':
      return 'Reception'
    case 'onboarding':
      return 'Onboarding'
    case 'finance':
      return 'Finance'
    case 'client':
      return 'Client'
    case 'sales':
      return 'Sales'
    case 'operations':
      return 'Operations'
    case 'supervisor':
      return 'Supervisor'
    default: {
      const _exhaustive: never = desk
      return _exhaustive
    }
  }
}
